function debounce(fn, wait) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  };
}

export class CollectionInfo extends HTMLElement {
  constructor() {
    super();
    this.debounceOnChange = debounce((event) => this.onChangeHandler(event), 1000);
    this.addEventListener('change', this.uncheckCategoryChildren);
    this.addEventListener('change', this.debounceOnChange.bind(this));
    this.addEventListener('click', this.onClickHandler.bind(this));
    
  }

  onClickHandler = (event) => {
    if (event.target.matches('[data-render-section-url]')) {
      event.preventDefault();
      const searchParams = new URLSearchParams(event.target.dataset.renderSectionUrl.split('?')[1]).toString();
      this.fetchSection(searchParams);
    }
  };

  // Unticking a category also unticks its subcategories and types, so no hidden
  // drill-down filter stays in the URL. Values hold full paths like "Tops > Outerwear".
  uncheckCategoryChildren = (event) => {
    const input = event.target;
    if (input.type !== 'checkbox' || input.checked || !input.form) return;

    const childPrefix = `${input.value} > `;
    input.form.querySelectorAll('input[type="checkbox"]:checked').forEach((child) => {
      if (child.value.startsWith(childPrefix)) child.checked = false;
    });
  };

  onChangeHandler = (event) => {
    if (!event.target.matches('[data-render-section]')) return;

    const form = event.target.closest('form') || document.querySelector('#filters-form') || document.querySelector('#filters-form-drawer');
    const formData = new FormData(form);
    const searchParams = new URLSearchParams(formData);

    this.removeDefaultPriceFilters(searchParams, form);

    this.fetchSection(searchParams.toString());
  };

  get form() {
    return this.querySelector('collection-info form');
  }

  updateSourceFromDestination = (html, id) => {
    const source = html.getElementById(`${id}`);
    const destination = this.querySelector(`#${id}`);
    if (source && destination) {
      destination.innerHTML = source.innerHTML;
    }
  };

  removeDefaultPriceFilters = (searchParams, form) => {
    const priceMin = searchParams.get('filter.v.price.gte');
    const priceMax = searchParams.get('filter.v.price.lte');
    const actualMaxPrice = form.querySelector('.max-range')?.getAttribute('max');

    const isMinAtDefault = priceMin === '0' || !priceMin;
    const isMaxAtDefault = !actualMaxPrice || priceMax === actualMaxPrice || !priceMax;

    if (isMinAtDefault && isMaxAtDefault) {
      searchParams.delete('filter.v.price.gte');
      searchParams.delete('filter.v.price.lte');
    }
  };

  updateURL(searchParams) {
    history.pushState({}, '', `${window.location.pathname}?${searchParams}`);
  }

  updateFilters = (html, className) => {
    const filtersFromFetch = html.querySelectorAll(`collection-info .${className}`);
    const filtersFromDom = document.querySelectorAll(`collection-info .${className}`);

    // Remove filters that are no longer returned from the server
    Array.from(filtersFromDom).forEach((currentElement) => {
      if (!Array.from(filtersFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    Array.from(filtersFromFetch).forEach((elementToRender, index) => {
      document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
    });
  };

  updateInfiniteScroll = (html) => {
    const nextInfiniteScroll = html.querySelector('collection-info infinite-scroll');
    const currentInfiniteScroll = this.querySelector('infinite-scroll');

    if (currentInfiniteScroll && nextInfiniteScroll) {
      currentInfiniteScroll.replaceWith(nextInfiniteScroll);
      return;
    }

    if (currentInfiniteScroll && !nextInfiniteScroll) {
      currentInfiniteScroll.remove();
    }
  };

  toggleDisplay = (selector, display) => {
    const el = this.querySelector(selector);
    if (el) el.style.display = display;
  };

  showLoadingOverlay = () => {
    const section = this.dataset.section;

    this.toggleDisplay(`#loading-overlay-${section}`, 'flex');
    this.toggleDisplay(`#loading-spinner-${section}`, 'block');
    this.toggleDisplay(`#results-count-${section}`, 'none');
    this.toggleDisplay(`#drawer-results-count-${section}`, 'none');
    this.toggleDisplay(`#drawer-loading-spinner-${section}`, 'block');
  };

  hideLoadingOverlay = () => {
    const section = this.dataset.section;

    this.toggleDisplay(`#loading-overlay-${section}`, 'none');
    this.toggleDisplay(`#loading-spinner-${section}`, 'none');
    this.toggleDisplay(`#results-count-${section}`, 'block');
    this.toggleDisplay(`#drawer-results-count-${section}`, 'block');
    this.toggleDisplay(`#drawer-loading-spinner-${section}`, 'none');
  };

  fetchSection = (searchParams) => {
    this.showLoadingOverlay();
    fetch(`${window.location.pathname}?section_id=${this.dataset.section}&${searchParams}`)
      .then((response) => response.text())
      .then((responseText) => {
        let html = new DOMParser().parseFromString(responseText, 'text/html');
        this.updateURL(searchParams);
        this.updateSourceFromDestination(html, `product-grid-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `results-count-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `drawer-results-count-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `active-filters-count-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `active-filter-group-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `sort-by-drawer-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `sort-by-${this.dataset.section}`);
        this.updateSourceFromDestination(html, `filters-drawer-buttons-wrapper-id`);
        this.updateFilters(html, `js-filter`);
        this.updateInfiniteScroll(html);
        this.hideLoadingOverlay();
        this.scrollToProductGrid();
        document.dispatchEvent(new CustomEvent('malbon:collection-updated', { bubbles: true }));
      })
      .catch((error) => {
        console.error(error);
        this.hideLoadingOverlay();
      });
  };

  scrollToProductGrid = () => {
    const productGrid = this.querySelector(`#product-grid-${this.dataset.section}`);
    const headerId = document.querySelector('#main-header');
    const headerOffset = (headerId.offsetHeight + 10) || 80;

    if (productGrid) {
      const elementPosition = productGrid.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };
}

if (!customElements.get('collection-info')) {
  customElements.define('collection-info', CollectionInfo);
}